<template>
    <div class="single-attribute-wrapper">
        <div class="single-attribute">
            <div class="remove-button" v-if="showRemoveButton">
                <md-button class="md-icon-button md-mini" @click="onRemoved">
                    <md-icon>delete_outline</md-icon>
                </md-button>
            </div>
            <div class="single-attribute-row">
                <div class="label">{{ $t("chatbotv2.set") }}</div>
                <a-popover v-model="visible" trigger="click" :overlayClassName="'no-padding'">
                    <div slot="content">
                        <AttributePicker :default-selected="clone.attribute" :pick="onPicked" />
                    </div>
                    <div class="attribute-input">
                        <div class="attribute-input-text-wrapper">
                            <div class="attribute-input-placeholder" v-if="$_.isEmpty(clone.attribute)">
                                {{ $t("chatbotv2.chooseAttributeType") }}
                            </div>
                            <div class="single-attribute-style" v-else :class="{ active: visible }">
                                <span class="single-attribute-style">
                                    <AttributeIconPreview :attribute="clone.attribute" />
                                    <span class="ml-2"><AttributeLabel :attribute="clone.attribute" /></span>
                                </span>
                            </div>
                        </div>
                        <span class="material-icons" v-if="!$_.isEmpty(clone.attribute)"> expand_more </span>
                    </div>
                </a-popover>
            </div>
            <div class="modes">
                <div class="mode" :class="{ selected: clone.mode === 'static' }" @click="() => setMode('static')">
                    static
                </div>
                <div class="mode" :class="{ selected: clone.mode === 'dynamic' }" @click="() => setMode('dynamic')">
                    dynamic
                </div>
                <div class="mode" :class="{ selected: clone.mode === 'special' }" @click="() => setMode('special')">
                    special
                </div>
            </div>
            <div class="single-attribute-row">
                <div class="label">{{ $t("chatbotv2.to") }}</div>
                <div
                    v-if="
                        CONFIG.AKINON.enabled &&
                        clone.attribute.startsWith('akinon') &&
                        clone.attribute === 'akinon_brand'
                    "
                    class="akinon-brands"
                >
                    <div
                        class="akinon-brand"
                        :class="{ selected: CONFIG.AKINON.BRAND_TYPES.US_POLO_ASSN === clone.value }"
                        @click="() => setValue(CONFIG.AKINON.BRAND_TYPES.US_POLO_ASSN)"
                    >
                        US Polo ASSN
                    </div>
                    <div
                        class="akinon-brand"
                        :class="{ selected: CONFIG.AKINON.BRAND_TYPES.PIERRE_CARDIN === clone.value }"
                        @click="() => setValue(CONFIG.AKINON.BRAND_TYPES.PIERRE_CARDIN)"
                    >
                        Pierre Cardin
                    </div>
                    <div
                        class="akinon-brand"
                        :class="{ selected: CONFIG.AKINON.BRAND_TYPES.CACHAREL === clone.value }"
                        @click="() => setValue(CONFIG.AKINON.BRAND_TYPES.CACHAREL)"
                    >
                        Cacharel
                    </div>
                </div>
                <div v-else-if="clone.attribute === 'probel_kimlik_tipi'" class="akinon-brands">
                    <div
                        class="akinon-brand"
                        :class="{ selected: CONFIG.PROBEL.IDENTITY_TYPES.TC === clone.value }"
                        @click="() => setValue(CONFIG.PROBEL.IDENTITY_TYPES.TC)"
                    >
                        TC Vatandaşı
                    </div>
                    <div
                        class="akinon-brand"
                        :class="{ selected: CONFIG.PROBEL.IDENTITY_TYPES.FOREIGN === clone.value }"
                        @click="() => setValue(CONFIG.PROBEL.IDENTITY_TYPES.FOREIGN)"
                    >
                        Yabancı Uyruklu
                    </div>
                </div>
                <div
                    v-else-if="clone.attribute === 'probel_hasta_cinsiyet' && clone.mode === 'static'"
                    class="akinon-brands"
                >
                    <div
                        class="akinon-brand"
                        :class="{ selected: CONFIG.PROBEL.GENDER_TYPES.MALE === clone.value }"
                        @click="() => setValue(CONFIG.PROBEL.GENDER_TYPES.MALE)"
                    >
                        Erkek
                    </div>
                    <div
                        class="akinon-brand"
                        :class="{ selected: CONFIG.PROBEL.GENDER_TYPES.FEMALE === clone.value }"
                        @click="() => setValue(CONFIG.PROBEL.GENDER_TYPES.FEMALE)"
                    >
                        Kadın
                    </div>
                </div>

                <div v-else>
                    <div v-if="clone.mode === 'special'">
                        <a-radio-group :value="$_.get(clone, 'special.type')">
                            <a-radio value="random-number" @click="setSpecialType('random-number')">
                                Random Number
                            </a-radio>
                            <a-radio value="random-string" @click="setSpecialType('random-string')">
                                Random String
                            </a-radio>
                            <a-radio value="random-alphanumeric" @click="setSpecialType('random-alphanumeric')">
                                Random Alphanumeric
                            </a-radio>
                            <a-radio value="random-otp" @click="setSpecialType('random-otp')"> Random OTP</a-radio>
                        </a-radio-group>
                    </div>

                    <CustomPicker
                        v-else-if="clone.attribute === 'owner'"
                        v-model="clone.value"
                        :items="ownerOptions"
                        idKey="_id"
                        labelKey="name"
                        title="Sahip Seç"
                        icon="person"
                    />

                    <TiptapEditor
                        v-else-if="clone.mode === 'dynamic'"
                        :placeholder="$t('chatbotv2.typeAttributeValue')"
                        :show-attributes="clone.mode === 'dynamic'"
                        :show-emoji="true"
                        :max-character-count="1024"
                        v-model="clone.value"
                    />
                    <TiptapEditor
                        :placeholder="$t('chatbotv2.typeAttributeValue')"
                        :show-attributes="clone.mode === 'dynamic'"
                        :show-emoji="true"
                        :max-character-count="1024"
                        v-model="clone.value"
                        v-else
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AttributePicker from "../pickers/AttributePicker";
import TiptapEditor from "./TiptapEditor";
import log2 from "../../../../../../utils/log2";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import AttributeIconPreview from "./AttributeIconPreview";
import CONFIG from "../../../../../../config/config";
import AttributeLabel from "./tiptap-extensions/AttributeLabel";
import CustomPicker from "../../../../../../components/pickers/CustomPicker";

const log = log2("AttributeSetUpdater");
export default {
    name: "AttributeSetUpdater",
    components: { AttributePicker, TiptapEditor, AttributeIconPreview, AttributeLabel, CustomPicker },
    props: {
        value: {
            type: Object,
            required: true,
            default: {
                attribute: "",
                value: "",
                mode: "static", // static, dynamic,special
                special: {
                    type: null,
                },
            },
        },
        showRemoveButton: {
            type: Boolean,
            default: true,
            required: false,
        },
        onRemoved: {
            type: Function,
            required: false,
            default: () => {},
        },
    },
    data() {
        return {
            visible: false,
            clone: _.cloneDeep(this.value),
            CONFIG,
        };
    },
    computed: {
        ...mapGetters({
            board: "CB/board",
        }),
        ...mapState({
            site: state => state.site.site,
            users: state => state.user.users.data,
        }),
        ownerOptions() {
            const options = [
                {
                    _id: null,
                    name: "Sahipsiz"
                },
                ...(this.users || []).map(user => ({
                    _id: user._id,
                    name: user.name
                }))
            ];
            return options;
        }
    },
    methods: {
        ...mapActions({
            updateBoardSingleKey: "CB/updateBoardSingleKey",
        }),
        setValue(value) {
            this.clone.value = value;
        },
        onPicked(newAttribute) {
            log("onPicked", newAttribute);
            this.visible = false;
            this.clone.attribute = newAttribute;
            
            // Owner attribute için mode'u static'e zorla
            if (newAttribute === 'owner') {
                this.setMode('static');
            }

            const storyId = this.$route.params.id,
                version = this.$route.params.version,
                attributes = _.uniq([...this.board(storyId).state.attributes, newAttribute]);
            this.updateBoardSingleKey({
                id: storyId,
                version,
                key: "state",
                value: {
                    ...this.board(storyId).state,
                    attributes,
                },
            });
        },
        setMode(mode) {
            log("setMode", mode);
            this.clone.mode = mode;
            if (mode === "static") {
                this.clone.value = "";
            } else if (mode === "dynamic") {
                this.clone.value = "{{message}}";
            } else if (mode === "special") {
                this.clone.value = "";
                this.setSpecialType("random-number");
            }
            this.clone = _.cloneDeep(this.clone);
            this.$forceUpdate();
        },
        setSpecialType(type) {
            log("setSpecialType", type);
            this.clone = _.set(this.clone, "special.type", type);
            this.clone = _.cloneDeep(this.clone);
            this.$forceUpdate();
        },
    },
    mounted() {},
    watch: {
        clone: {
            handler(newVal) {
                this.$emit("input", newVal);
            },
            deep: true,
        },
        visible(isVisible) {
            const attributeFormIsEnabled = !!document.getElementById("add-custom-attr-modal");
            log("visible changed", { isVisible, attributeFormIsEnabled });
            // if (!isVisible && attributeFormIsEnabled) {
            //     this.visible = true;
            // }
        },
    },
};
</script>

<style lang="scss">
.single-attribute-wrapper {
    padding: 0 30px;

    .single-attribute {
        background: #fff;
        border-radius: 4px;
        padding: 20px;
        position: relative;
        width: 100%;

        .editable-editor-wrapper {
            border: 1px solid #c6cdd4 !important;

            &:hover {
                border: 1px solid #006cff !important;
            }

            &.focused {
                border: 1px solid #006cff !important;
            }

            .editable-editor {
                border-radius: unset !important;
                overflow-x: hidden;
                overflow-y: auto;

                .ProseMirror {
                    height: 100%;
                }

                &.focused {
                    border: unset !important;
                }

                &:focus {
                    border: unset !important;
                }

                &:hover {
                    border: unset !important;
                    resize: none;
                }
            }
        }

        &:hover {
            .remove-button {
                opacity: 1;
            }
        }

        .remove-button {
            left: -43px;
            opacity: 0;
            position: absolute;
            top: 0;
            transition: opacity 0.3s;

            .md-button {
                background-color: #fff;
                align-items: center;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                cursor: pointer;
                display: flex;
                justify-content: center;
                margin-bottom: 7px;
                transition: background-color 0.3s;
                height: 30px;
                width: 30px;
                min-height: 30px !important;
                min-width: 30px !important;

                .md-icon {
                    color: #006cff;
                }
            }
        }

        .single-attribute-row {
            grid-column-gap: 20px;
            align-items: center;
            display: grid;
            grid-template-columns: 30px minmax(0, 1fr);
            margin-bottom: 15px;

            .label {
                font-weight: 600;
                text-align: right;
            }

            .attribute-input {
                align-items: center;
                background: #fff;
                border: 1px solid #c6cdd4;
                border-radius: 6px;
                cursor: pointer;
                display: flex;
                height: 43px;
                padding: 15px;
                position: relative;
                transition: all 0.3s;
                user-select: none;
                width: 100%;

                &:hover {
                    border: 1px solid #006cff !important;
                }

                .attribute-input-text-wrapper {
                    display: flex;
                    flex-grow: 1;
                    width: 0;
                }

                .attribute-input-placeholder {
                    color: #9ba6b3;
                    font-size: 15px;
                    line-height: 19px;
                    overflow: hidden !important;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .single-attribute-style {
                    background: #ffdc66;
                    cursor: pointer;
                    transition: all 0.3s;
                    border-radius: 10px;
                    box-decoration-break: clone;
                    padding: 0 3px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    font-weight: 800;

                    &.active {
                        background: #006cff;
                        color: #fff;

                        .material-icons {
                            color: #fff;
                        }
                    }

                    .material-icons {
                        color: #647283;
                        font-size: 18px;
                        margin-right: 3px;
                    }
                }
            }
        }
    }

    .akinon-brands {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .akinon-brand {
            padding: 10px;
            border: 1px solid #c6cdd4;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            cursor: pointer;

            &.selected {
                border: 1px solid #006cff;
                background: #006cff;
                color: #fff;
            }
        }
    }

    .modes {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin-bottom: 5px;

        .mode {
            padding: 5px;
            border: 1px solid #c6cdd4;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            cursor: pointer;

            &.selected {
                border: 1px solid #006cff;
                background: #006cff;
                color: #fff;
            }
        }
    }

    .ant-radio-group {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }
}
</style>
